import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Sparkles, Wand2, Eye, Loader2, GitBranch, Check, CheckCircle2, Circle,
  ExternalLink, AlertTriangle, Trash2, ChevronDown, ChevronRight, FileCode, FileText, Paperclip, Undo2, XCircle,
} from 'lucide-react';
import { api, apiFetch, ApiError } from '../../lib/api';
import { AUTO_RESOLVE_PHASES, AUTO_RESOLVE_ACTIONS } from '../../config/autoResolve';

const { IDLE, RUNNING, PR_OPEN, MERGED, FAILED, NEEDS_HUMAN } = AUTO_RESOLVE_PHASES;

// Friendly status lines that cycle while the agent is working (RUNNING phase).
const RUNNING_MESSAGES = [
  'Reading the request…',
  'Finding the content on the page…',
  'Drafting the edit…',
  'Opening a live preview…',
];

// Turn a repo file path into a friendly label, e.g.
// "src/pages/HomePage.jsx" -> "Homepage". Best-effort; falls back to the path.
function friendlyFileName(path) {
  const base = String(path || '').split('/').pop() || path;
  const stem = base.replace(/\.[A-Za-z0-9]+$/, '');
  const labels = {
    HomePage: 'Homepage', Home: 'Homepage', AboutPage: 'About page', About: 'About page',
    ContactPage: 'Contact page', Contact: 'Contact page', App: 'Site shell',
    Navbar: 'Navigation bar', Footer: 'Footer',
  };
  if (labels[stem]) return labels[stem];
  // CamelCase / kebab / snake -> spaced words.
  const words = stem
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : base;
}

// Poll cadence (ms) by phase. Faster while the agent is working, slower while
// we wait for the Cloudflare preview to build.
const POLL_DELAY = {
  [RUNNING]: 4000,
  [PR_OPEN]: 10000,
  [IDLE]: 8000,
};
const DEFAULT_POLL_DELAY = 8000;

// After we optimistically fire a dispatch, GitHub takes a few seconds to
// register the workflow run. During that gap getAutoResolveState still reports
// `idle`; we hold the UI in RUNNING for up to this long so the Apply button
// stays hidden and a second dispatch can't slip through.
const DISPATCH_GRACE_MS = 90000;

// One concise sentence per phase for the aria-live region (screen readers).
function liveSentence(phase, done, deploying, deployFailed = false) {
  if (deployFailed) return 'The site build failed after publishing; the previous version is still live';
  if (deploying) return 'Publishing the update to the live site';
  if (done) return 'Update published to the live site';
  switch (phase) {
    case RUNNING: return 'The AI agent is drafting your change';
    case PR_OPEN: return 'The change is drafted and ready to preview and publish';
    case MERGED: return 'Update published to the live site';
    case FAILED: return 'Auto-resolve failed';
    case NEEDS_HUMAN: return 'The assistant needs more detail before it can make this change';
    default: return 'Auto-resolve idle';
  }
}

export default function AutoResolvePanel({ editRequest }) {
  const [lifecycle, setLifecycle] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoError, setAutoError] = useState('');
  const [refreshError, setRefreshError] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState(false);
  // Raw-diff "View the exact code change" disclosure (collapsed by default).
  const [diffOpen, setDiffOpen] = useState(false);
  // Index into RUNNING_MESSAGES for the cycling "agent is working" line.
  const [runMsgIndex, setRunMsgIndex] = useState(0);
  // needs_human "answer the question" affordance: a reply + optional file(s).
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);

  // Timestamp of the last optimistic dispatch (0 = none in flight).
  const dispatchedRef = useRef(0);

  const editRequestId = editRequest?.id;
  const accountName = editRequest?.accountName || 'this';

  // Fetch live state from GitHub. The dispatch guard bridges the
  // run-registration gap so the Apply button never reappears mid-run.
  const refetchLifecycle = useCallback(async () => {
    if (!editRequestId) return null;
    try {
      const data = await api.get(`/api/admin/edit-requests/${editRequestId}/auto-resolve`);
      setRefreshError(false);
      let resolved = data;
      setLifecycle((prev) => {
        if (dispatchedRef.current) {
          const within = Date.now() - dispatchedRef.current < DISPATCH_GRACE_MS;
          if (data.phase === IDLE && within) {
            resolved = { ...data, phase: RUNNING };
            return resolved;
          }
          if (!within || [RUNNING, PR_OPEN, MERGED, FAILED].includes(data.phase)) {
            dispatchedRef.current = 0;
          }
        }
        resolved = data;
        return data;
      });
      return resolved;
    } catch {
      setRefreshError(true);
      return null;
    }
  }, [editRequestId]);

  // Self-scheduling poll loop. Delay varies by phase; stops when published.
  useEffect(() => {
    if (!editRequestId) return undefined;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      const d = await refetchLifecycle();
      if (cancelled) return;
      // The merge webhook sets requestStatus='resolved' the instant the PR merges
      // — BEFORE Cloudflare finishes the production deploy. So don't stop on that
      // alone: keep polling while phase==='merged' && deploying (to catch the
      // deploy completing) and stop only once truly live (merged && !deploying).
      const phase = d?.phase;
      // Server publish state wins; a failed deploy also ends the poll (there is
      // nothing further to wait for — a human has to act).
      const publishState = d?.publish?.state || null;
      const deploying = publishState ? publishState === 'deploying' : !!d?.deploying;
      const live = d && phase === MERGED && (!deploying || publishState === 'deploy_failed'); // production deploy finished (or failed)
      if (live) return; // stop polling
      // While the deploy is in flight, poll faster (~8s) so we catch it going live.
      const delay = phase === MERGED && deploying ? 8000 : (POLL_DELAY[phase] ?? DEFAULT_POLL_DELAY);
      timer = setTimeout(tick, delay);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [editRequestId, refetchLifecycle]);

  // Reset transient UI when the edit request changes.
  useEffect(() => {
    setLifecycle(null);
    setAutoBusy(false);
    setAutoError('');
    setRefreshError(false);
    setConfirmMerge(false);
    setConfirmDiscard(false);
    setConfirmRollback(false);
    setReplyText('');
    setReplyFiles([]);
    setDiffOpen(false);
    setRunMsgIndex(0);
    dispatchedRef.current = 0;
  }, [editRequestId]);

  // Cycle the friendly "agent is working" status line every ~2.5s, but only
  // while the run is active. Resets to the first line whenever we leave RUNNING.
  const isRunningPhase = lifecycle?.phase === RUNNING;
  useEffect(() => {
    if (!isRunningPhase) {
      setRunMsgIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setRunMsgIndex((i) => (i + 1) % RUNNING_MESSAGES.length);
    }, 2500);
    return () => clearInterval(id);
  }, [isRunningPhase]);

  // Dispatch the agent. Carries OPTIONAL admin context — extra instructions +
  // reference file(s) the client's original request may lack — entered on the
  // Apply-update card or the needs_human "answer the question" card.
  const runAutoResolve = async () => {
    if (autoBusy || !editRequestId) return;
    setAutoBusy(true);
    setAutoError('');
    try {
      let addedAttachments = [];
      if (replyFiles.length) {
        const fd = new FormData();
        fd.append('scope', 'edit-requests');
        replyFiles.forEach((f) => fd.append('files', f));
        const up = await apiFetch(
          `/api/upload?accountId=${encodeURIComponent(editRequest?.accountId || '')}`,
          { method: 'POST', body: fd }
        );
        addedAttachments = (up && up.attachments) || [];
      }
      await api.post(`/api/admin/edit-requests/${editRequestId}/auto-resolve`, {
        action: AUTO_RESOLVE_ACTIONS.RESOLVE,
        replyText: replyText.trim() || undefined,
        addedAttachments,
      });
      // Optimistically hold RUNNING until GitHub registers the run.
      dispatchedRef.current = Date.now();
      setReplyText('');
      setReplyFiles([]);
      setLifecycle((l) => ({ ...(l || {}), configured: true, phase: RUNNING, pr: null, diff: null, needsHumanReason: null }));
      setTimeout(() => { refetchLifecycle(); }, 2500);
    } catch (err) {
      setAutoError(err instanceof ApiError ? err.message : 'Couldn’t start the update.');
    } finally {
      setAutoBusy(false);
    }
  };

  const mergeUpdate = async () => {
    if (autoBusy || !editRequestId) return;
    if (!confirmMerge) { setConfirmMerge(true); return; }
    setConfirmMerge(false);
    setAutoBusy(true);
    setAutoError('');
    try {
      // F7 — pin the publish to the commit that was reviewed on this screen.
      await api.post(`/api/admin/edit-requests/${editRequestId}/auto-resolve`, {
        action: AUTO_RESOLVE_ACTIONS.MERGE,
        ...(lifecycle?.pr?.headSha ? { expectedHeadSha: lifecycle.pr.headSha } : {}),
      });
      await refetchLifecycle();
    } catch (err) {
      setAutoError(err instanceof ApiError ? err.message : 'Couldn’t publish the update.');
    } finally {
      setAutoBusy(false);
    }
  };

  const discardUpdate = async () => {
    if (autoBusy || !editRequestId) return;
    if (!confirmDiscard) { setConfirmDiscard(true); return; }
    setConfirmDiscard(false);
    setAutoBusy(true);
    setAutoError('');
    try {
      await api.post(`/api/admin/edit-requests/${editRequestId}/auto-resolve`, { action: AUTO_RESOLVE_ACTIONS.DISCARD });
      dispatchedRef.current = 0;
      await refetchLifecycle();
    } catch (err) {
      setAutoError(err instanceof ApiError ? err.message : 'Couldn’t discard the update.');
    } finally {
      setAutoBusy(false);
    }
  };

  // Roll back a PUBLISHED update — revert the live site to its pre-update state.
  // Only offered (and only succeeds) when this was the latest deployment.
  const rollbackUpdate = async () => {
    if (autoBusy || !editRequestId) return;
    setConfirmRollback(false);
    setAutoBusy(true);
    setAutoError('');
    try {
      await api.post(`/api/admin/edit-requests/${editRequestId}/auto-resolve`, { action: AUTO_RESOLVE_ACTIONS.ROLLBACK });
      dispatchedRef.current = 0;
      // The live change is reverted; the request returns to Apply-update (idle).
      setLifecycle((l) => ({ ...(l || {}), phase: IDLE, pr: null, diff: null, previewUrl: null, canRollback: false, requestStatus: 'open' }));
      setTimeout(() => { refetchLifecycle(); }, 1500);
    } catch (err) {
      setAutoError(err instanceof ApiError ? err.message : 'Couldn’t roll back this update. Please try again.');
    } finally {
      setAutoBusy(false);
    }
  };

  if (!editRequest) return null;

  const phase = lifecycle?.phase || IDLE;
  const requestResolved = lifecycle?.requestStatus === 'resolved';
  const done = requestResolved || phase === MERGED;
  // Once merged, Cloudflare is still building the production deploy until
  // `deploying` clears. Distinguish "publishing…" (deploying) from "live".
  // F7 — the server's verified publish state is authoritative when present:
  // 'deploying' (build running), 'live' (production deploy succeeded — the
  // request is resolved and the school emailed), 'deploy_failed' (build broke;
  // the old site is still live and the school was NOT told it's done).
  const publish = lifecycle?.publish?.state || null;
  const isDeployFailed = phase === MERGED && publish === 'deploy_failed';
  const isDeploying = phase === MERGED && !isDeployFailed && (publish ? publish === 'deploying' : !!lifecycle?.deploying);
  const isLive = phase === MERGED && !isDeploying && !isDeployFailed;
  const publishUnverifiable = isDeploying && lifecycle?.publish?.reason === 'no_checks_permission';
  const configured = lifecycle?.configured;
  const busyRunning = autoBusy || phase === RUNNING;

  // Step index mapping for the progress stepper.
  const STEPS = ['Dispatch', 'Agent editing', 'Review & publish', 'Published'];
  const changedFiles = Array.isArray(lifecycle?.changedFiles) ? lifecycle.changedFiles : [];
  const stat = lifecycle?.stat || null;
  let currentStep = 0; // 0-based index of the active step
  let failedStep = -1;
  if (isDeployFailed) { currentStep = 3; failedStep = 3; }
  else if (isDeploying) currentStep = 3; // "Published" is the in-progress step (CF deploying)
  else if (done) currentStep = 4; // all complete (truly live)
  else if (phase === PR_OPEN) currentStep = 2;
  else if (phase === RUNNING) currentStep = 1;
  else if (phase === FAILED) { currentStep = 1; failedStep = lifecycle?.pr ? 2 : 1; }

  const showApplyCta =
    configured && (phase === IDLE || phase === FAILED || phase === NEEDS_HUMAN) && !autoBusy && !requestResolved;

  return (
    <div
      className="pt-3 border-t border-white/[0.06]"
      aria-busy={busyRunning ? 'true' : 'false'}
    >
      {/* Hero header */}
      <div className="flex items-center gap-3 mb-3">
        <span
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#B0FC38]/15 border border-[#B0FC38]/40 shadow-[0_0_16px_rgba(176,252,56,0.35)] shrink-0"
          aria-hidden="true"
        >
          <Sparkles className="w-[18px] h-[18px] text-[#B0FC38]" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold text-white leading-tight">AI Auto-Resolve</h3>
            <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#B0FC38]/20 to-[#B0FC38]/20 border border-white/10 text-[9px] font-semibold uppercase tracking-wide text-[#B0FC38]">Beta</span>
          </div>
          <p className="text-[12px] text-gray-400 leading-snug">
            An AI agent drafts the change. You preview it live, then publish.
          </p>
        </div>
      </div>

      {/* Screen-reader live status */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {configured ? liveSentence(phase, done, isDeploying, isDeployFailed) : ''}
      </div>

      {autoError && (
        <div className="px-3 py-2 mb-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[12px] text-red-300/90">
          {autoError}
        </div>
      )}

      {refreshError && (
        <div className="mb-2 text-[11px] text-amber-300/70">⚠ Couldn’t refresh, retrying…</div>
      )}

      {/* Not wired up for this account */}
      {lifecycle && !configured && !requestResolved && (
        <div className="px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06] text-[12px] text-gray-400">
          Auto-resolve isn’t set up for this account’s website yet.
        </div>
      )}

      {/* IDLE (pre-dispatch): just the CTA + explainer, no stepper */}
      {configured && phase === IDLE && !requestResolved && (
        <div className="rounded-xl border border-white/[0.08] bg-[#0a0a0a] px-4 py-4">
          <p className="text-[12px] text-gray-400 mb-3 leading-relaxed">
            The assistant will read this request, make the edit on a copy of the{' '}
            <span className="text-gray-200">{accountName}</span> website, and show you a preview
            to review before anything goes live.
          </p>
          {/* Optional admin context the client's request may not spell out. */}
          <InstructionsFields
            value={replyText} onChange={setReplyText}
            files={replyFiles} onFiles={setReplyFiles}
            disabled={autoBusy}
            label={<>Add instructions for the agent <span className="text-gray-600 font-normal">(optional)</span></>}
            placeholder="e.g. The “photo” they mean is the hero image. Use the school’s navy, not the old teal."
          />
          {showApplyCta ? (
            <button
              type="button"
              onClick={runAutoResolve}
              disabled={autoBusy}
              aria-label={`Apply this update to the ${accountName} website`}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Wand2 className="w-3.5 h-3.5" /> Apply update
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 text-[12px] text-gray-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Starting…
            </span>
          )}
        </div>
      )}

      {/* NEEDS_HUMAN: the agent asked a question instead of guessing wrong. */}
      {configured && phase === NEEDS_HUMAN && !requestResolved && (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] px-4 py-4">
          <div className="flex items-start gap-2.5 mb-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0">
              <h4 className="text-[13px] font-semibold text-amber-100 leading-tight">The assistant needs a bit more detail</h4>
              <p className="text-[12px] text-amber-200/80 leading-snug mt-0.5">
                It didn’t want to guess and change the wrong thing, so it’s asking:
              </p>
            </div>
          </div>
          <p className="text-[13px] text-gray-100 leading-relaxed px-3 py-2.5 rounded-lg bg-black/30 border border-white/[0.06] mb-3">
            {lifecycle?.needsHumanReason || 'Could you clarify what to change and where?'}
          </p>
          {/* Answer the question right here: a reply + optional file(s). */}
          <InstructionsFields
            value={replyText} onChange={setReplyText}
            files={replyFiles} onFiles={setReplyFiles}
            disabled={autoBusy}
            label="Your answer"
            placeholder="e.g. Replace the About-section photo, the new image is attached."
          />
          {showApplyCta ? (
            <button
              type="button"
              onClick={runAutoResolve}
              disabled={autoBusy || (!replyText.trim() && replyFiles.length === 0)}
              aria-label={`Send your answer and re-apply the update to the ${accountName} website`}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Wand2 className="w-3.5 h-3.5" /> Send answer &amp; re-apply
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 text-[12px] text-gray-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Starting…
            </span>
          )}
          <p className="text-[11px] text-gray-500 leading-relaxed mt-2">
            Or mark the exact spot in the Visual Editor. That counts as an explicit answer.
          </p>
        </div>
      )}

      {/* Active card: stepper + phase content. A faint purple→lime gradient
          border (the wrapper's padding becomes a 1px frame) marks it as the
          special AI surface without clashing with the dark modal. */}
      {configured && phase !== IDLE && phase !== NEEDS_HUMAN && (
        <div className="rounded-xl p-px bg-gradient-to-br from-[#B0FC38]/40 via-white/[0.06] to-[#B0FC38]/30 shadow-[0_-6px_24px_-12px_rgba(176,252,56,0.45)]">
        <div className="rounded-[11px] border border-white/[0.04] bg-[#0a0a0a] overflow-hidden">
          {/* Progress stepper */}
          <div className="px-4 pt-3.5 pb-3 border-b border-white/[0.06]">
            <Stepper steps={STEPS} currentStep={currentStep} failedStep={failedStep} />
          </div>

          {/* Branch + status pill row */}
          <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-white/[0.06]">
            <div className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 min-w-0">
              <GitBranch className="w-3 h-3 shrink-0" />
              <span className="font-mono truncate">{lifecycle?.branch || '—'}</span>
            </div>
            <StatusPill phase={phase} done={done} deploying={isDeploying} deployFailed={isDeployFailed} />
          </div>

          {/* RUNNING — alive "agent is working" treatment with a cycling line */}
          {phase === RUNNING && (
            <div className="px-4 py-5 flex items-center gap-3 text-[13px] text-gray-200">
              <span className="relative inline-flex items-center justify-center w-8 h-8 shrink-0" aria-hidden="true">
                <span className="absolute inset-0 rounded-full bg-[#B0FC38]/20 animate-ping" />
                <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#B0FC38]/15 border border-[#B0FC38]/40">
                  <Sparkles className="w-4 h-4 text-[#B0FC38] animate-pulse" />
                </span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-[#B0FC38]">The agent is editing the site…</span>
                <span className="block text-[12px] text-gray-400 transition-opacity">{RUNNING_MESSAGES[runMsgIndex]}</span>
              </span>
              {lifecycle?.run?.url && (
                <a href={lifecycle.run.url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-[12px] text-[#B0FC38] hover:text-white inline-flex items-center gap-1" aria-label="View the agent run on GitHub">run <ExternalLink className="w-3 h-3" /></a>
              )}
            </div>
          )}

          {/* FAILED */}
          {phase === FAILED && (
            <div className="px-3 py-3 flex flex-wrap items-center justify-between gap-2 text-[12px] text-red-300/90">
              <span>
                The assistant couldn’t finish this update.{' '}
                {lifecycle?.run?.url && (
                  <a href={lifecycle.run.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">View details</a>
                )}{' '}· you can adjust the request and try again.
              </span>
              {showApplyCta && (
                <button
                  type="button"
                  onClick={runAutoResolve}
                  disabled={autoBusy}
                  aria-label={`Try the update again on the ${accountName} website`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Wand2 className="w-3.5 h-3.5" /> Try again
                </button>
              )}
            </div>
          )}

          {/* PR_OPEN / MERGED — clarity first: summary -> preview -> (collapsed) diff -> actions */}
          {(phase === PR_OPEN || phase === MERGED) && (
            <>
              {!lifecycle?.diff && (
                <div className="px-4 py-4 text-[12px] text-gray-400 inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading the proposed change…
                </div>
              )}

              {lifecycle?.diff && (
                <>
                  {/* Summary row: "Here's what the agent drafted" */}
                  <div className="px-4 pt-3.5 pb-3 border-b border-white/[0.06]">
                    <p className="text-[12px] font-medium text-gray-300 mb-2">Here’s what the agent drafted</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {(changedFiles.length ? changedFiles : ['the website']).map((path) => (
                        <span
                          key={path}
                          className="inline-flex items-center gap-1.5 max-w-full px-2.5 py-1 rounded-lg bg-[#B0FC38]/[0.08] border border-[#B0FC38]/25 text-[12px] text-gray-200"
                          title={path}
                        >
                          <FileCode className="w-3.5 h-3.5 text-[#B0FC38] shrink-0" />
                          <span className="font-medium">{friendlyFileName(path)}</span>
                          {changedFiles.length ? <span className="text-gray-500 font-mono text-[11px] truncate">({path})</span> : null}
                        </span>
                      ))}
                      {stat && (stat.additions || stat.deletions) ? (
                        <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[11px] font-mono">
                          <span className="text-[#B0FC38]">+{stat.additions}</span>
                          <span className="text-red-300">−{stat.deletions}</span>
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* Hero CTA: View live preview — the emotional peak */}
                  <div className="px-4 py-4 border-b border-white/[0.06]">
                    {lifecycle?.previewUrl ? (
                      <a
                        href={lifecycle.previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open the live preview of this change in a new tab"
                        className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#B0FC38]/50 bg-[#B0FC38]/[0.06] text-[#B0FC38] text-[14px] font-semibold hover:bg-[#B0FC38]/[0.12] hover:border-[#B0FC38] transition-all shadow-[0_0_18px_-6px_rgba(176,252,56,0.5)]"
                      >
                        <Eye className="w-[18px] h-[18px]" /> View live preview
                        <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
                      </a>
                    ) : phase === PR_OPEN ? (
                      <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-white/[0.08] bg-white/[0.02] text-gray-500 text-[14px] font-semibold cursor-default">
                        <Loader2 className="w-4 h-4 animate-spin" /> Building live preview…
                      </span>
                    ) : null}
                    {lifecycle?.previewUrl && (
                      <p className="mt-2 text-[12px] text-gray-400">
                        See your change on a real copy of the site before it goes live.
                      </p>
                    )}
                  </div>

                  {/* Collapsible raw diff — "View the exact code change" */}
                  <div className="border-b border-white/[0.06]">
                    <button
                      type="button"
                      onClick={() => setDiffOpen((o) => !o)}
                      aria-expanded={diffOpen}
                      className="w-full flex items-center gap-1.5 px-4 py-2.5 text-[12px] text-gray-400 hover:text-gray-200 transition-colors"
                    >
                      {diffOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      <FileText className="w-3.5 h-3.5" />
                      <span>View the exact code change</span>
                    </button>
                    {diffOpen && (
                      <>
                        <DiffView diff={lifecycle.diff} />
                        {lifecycle?.diffTruncated && (
                          <div className="px-3 pb-2 text-[11px] text-gray-500">Change list trimmed. Open the full version for everything.</div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}

              <div className="px-3 py-2.5 flex flex-col gap-2.5">
                {/* PR link (smaller) */}
                {lifecycle?.pr?.url && (
                  <div className="flex flex-wrap items-center gap-3 text-[11px]">
                    <a href={lifecycle.pr.url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white inline-flex items-center gap-1" aria-label={`Open change #${lifecycle.pr.number} on GitHub`}>Change #{lifecycle.pr.number} <ExternalLink className="w-3 h-3" /></a>
                  </div>
                )}

                {/* Merge confirm warning box (amber) */}
                {!done && confirmMerge && (
                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 text-[12px] text-amber-200/90">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
                    <span>
                      This publishes the change to the live {accountName} website right away. Visitors will see it within about a minute. You can roll it back from here if it’s wrong.
                    </span>
                  </div>
                )}

                {/* Rollback confirm warning box (amber) */}
                {confirmRollback && (
                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 text-[12px] text-amber-200/90">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
                    <span>
                      This reverts the live {accountName} website to how it was <em>before</em> this update. Visitors will see the previous version within about a minute.
                    </span>
                  </div>
                )}

                {/* Actions row */}
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {isDeployFailed ? (
                    <span className="inline-flex items-start gap-2 text-[12px] text-red-300 font-medium text-right" data-testid="auto-resolve-deploy-failed">
                      <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>
                        <span className="block">Site build failed after publishing</span>
                        <span className="block text-[11px] font-normal text-gray-400">The previous version is still live and the school has not been notified. Fix the build or roll back.</span>
                      </span>
                    </span>
                  ) : isDeploying ? (
                    <span className="inline-flex items-start gap-2 text-[12px] text-amber-200/90 font-medium text-right" data-testid="auto-resolve-deploying">
                      <Loader2 className="w-3.5 h-3.5 shrink-0 mt-0.5 animate-spin text-amber-300" />
                      <span>
                        <span className="block">Publishing to the live site…</span>
                        <span className="block text-[11px] font-normal text-gray-400">
                          {publishUnverifiable
                            ? 'Cloudflare is deploying, but the CRM cannot confirm completion — GITHUB_DISPATCH_TOKEN needs the “Checks: read” permission. The school is notified only once the deploy is confirmed.'
                            : 'Cloudflare is deploying your change (usually 2–10 minutes). The school is notified once it is confirmed live.'}
                        </span>
                      </span>
                    </span>
                  ) : isLive ? (
                    <div className="flex flex-wrap items-center justify-end gap-2 w-full">
                      <span className="inline-flex items-center gap-1 text-[12px] text-[#B0FC38] font-medium mr-auto"><CheckCircle2 className="w-3.5 h-3.5" /> Published &amp; live</span>
                      {lifecycle?.canRollback ? (
                        confirmRollback ? (
                          <div className="inline-flex items-center gap-2">
                            <button type="button" onClick={() => setConfirmRollback(false)} className="text-[12px] text-gray-400 hover:text-white">Keep it</button>
                            <button type="button" onClick={rollbackUpdate} disabled={autoBusy} aria-label={`Confirm reverting the live ${accountName} website to before this update`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 text-[12px] font-medium hover:bg-red-500/10 disabled:opacity-60">
                              {autoBusy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rolling back…</>) : (<><Undo2 className="w-3.5 h-3.5" /> Confirm: revert the live site</>)}
                            </button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setConfirmRollback(true)} disabled={autoBusy} aria-label="Roll back this published update on the live site" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] text-gray-400 hover:text-gray-200 border border-white/[0.08] hover:bg-white/[0.04] transition-colors disabled:opacity-60">
                            <Undo2 className="w-3.5 h-3.5" /> Roll back
                          </button>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500" title="Only the most recent change to a site can be one-click rolled back, to avoid clobbering a newer change.">
                          <Undo2 className="w-3 h-3 opacity-50" /> A newer change shipped after this. Roll that back first
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Discard */}
                      {confirmDiscard ? (
                        <div className="inline-flex items-center gap-2">
                          <button type="button" onClick={() => setConfirmDiscard(false)} className="text-[12px] text-gray-400 hover:text-white">Keep it</button>
                          <button type="button" onClick={discardUpdate} disabled={autoBusy} aria-label="Confirm discarding this draft update" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 text-[12px] font-medium hover:bg-red-500/10 disabled:opacity-60">
                            <Trash2 className="w-3.5 h-3.5" /> Discard draft
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => { setConfirmMerge(false); setConfirmDiscard(true); }} disabled={autoBusy} aria-label="Discard this draft update" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-60">
                          <Trash2 className="w-3.5 h-3.5" /> Discard
                        </button>
                      )}

                      {/* Merge */}
                      {confirmMerge ? (
                        <div className="inline-flex items-center gap-2">
                          <button type="button" onClick={() => setConfirmMerge(false)} className="text-[12px] text-gray-400 hover:text-white">Cancel</button>
                          <button type="button" onClick={mergeUpdate} disabled={autoBusy} aria-label={`Confirm publishing to the live ${accountName} website`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E] disabled:opacity-60">
                            {autoBusy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Publishing…</>) : (<><Check className="w-3.5 h-3.5" /> Confirm: publish to live site</>)}
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => { setConfirmDiscard(false); mergeUpdate(); }} disabled={autoBusy} aria-label="Review then publish this update to the live site" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E] disabled:opacity-60">
                          <Check className="w-3.5 h-3.5" /> Merge &amp; publish
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        </div>
      )}
    </div>
  );
}

// Optional admin-context input (extra instructions + reference file) shared by
// the Apply-update card and the needs_human answer card. Drives replyText/
// replyFiles, which runAutoResolve folds into the request before dispatching.
function InstructionsFields({ value, onChange, files, onFiles, disabled, label, placeholder }) {
  return (
    <>
      <label className="block text-[11px] font-medium text-gray-400 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={2}
        placeholder={placeholder}
        className="w-full rounded-lg bg-black/30 border border-white/[0.08] px-3 py-2 text-[13px] text-gray-100 placeholder:text-gray-500 focus:outline-none focus:border-white/25 resize-y mb-2"
      />
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-300 ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-white/[0.07]'}`}>
          <Paperclip className="w-3.5 h-3.5" />
          {files.length ? `${files.length} file${files.length > 1 ? 's' : ''} attached` : 'Attach a reference file'}
          <input
            type="file"
            multiple
            accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            disabled={disabled}
            onChange={(e) => onFiles(Array.from(e.target.files || []))}
            className="hidden"
          />
        </label>
        {files.length > 0 && (
          <>
            <span className="text-[11px] text-gray-500 truncate max-w-[180px]">{files.map((f) => f.name).join(', ')}</span>
            <button type="button" onClick={() => onFiles([])} disabled={disabled} className="text-[11px] text-gray-500 hover:text-gray-300">clear</button>
          </>
        )}
      </div>
    </>
  );
}

// Progress stepper: Dispatch -> Agent editing -> Review & publish -> Published.
// completed = filled lime check, current = purple ring + glow, future = dim.
function Stepper({ steps, currentStep, failedStep }) {
  return (
    <ol className="flex items-center gap-1.5" aria-label="Update progress">
      {steps.map((label, i) => {
        const isFailed = failedStep === i;
        const isComplete = !isFailed && i < currentStep;
        const isCurrent = !isFailed && i === currentStep;
        return (
          <React.Fragment key={label}>
            <li className="flex items-center gap-1.5 min-w-0">
              {isFailed ? (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500/15 border border-red-500/40 shrink-0" aria-hidden="true">
                  <AlertTriangle className="w-2.5 h-2.5 text-red-300" />
                </span>
              ) : isComplete ? (
                <CheckCircle2 className="w-4 h-4 text-[#B0FC38] shrink-0" aria-hidden="true" />
              ) : isCurrent ? (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border-2 border-[#B0FC38] shadow-[0_0_8px_rgba(176,252,56,0.6)] shrink-0" aria-hidden="true">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#B0FC38]" />
                </span>
              ) : (
                <Circle className="w-4 h-4 text-gray-700 shrink-0" aria-hidden="true" />
              )}
              <span
                className={`text-[11px] truncate ${
                  isFailed ? 'text-red-300'
                    : isComplete ? 'text-gray-300'
                      : isCurrent ? 'text-[#B0FC38] font-medium'
                        : 'text-gray-600'
                }`}
              >
                {label}
              </span>
            </li>
            {i < steps.length - 1 && (
              <li aria-hidden="true" className={`h-px flex-1 min-w-[8px] ${i < currentStep ? 'bg-[#B0FC38]/40' : 'bg-white/[0.08]'}`} />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

// Status pill mirroring the stepper (text + color, not color alone).
function StatusPill({ phase, done, deploying, deployFailed = false }) {
  const m = deployFailed
    ? { t: 'Deploy failed', c: 'text-red-300 bg-red-500/10 border-red-500/25' }
    : deploying
    ? { t: 'Publishing…', c: 'text-amber-200 bg-amber-500/10 border-amber-500/25' }
    : done
    ? { t: 'Published', c: 'text-[#B0FC38] bg-[#B0FC38]/10 border-[#B0FC38]/25' }
    : phase === PR_OPEN
      ? { t: 'Ready to review', c: 'text-[#B0FC38] bg-[#B0FC38]/10 border-[#B0FC38]/30' }
      : phase === FAILED
        ? { t: 'Failed', c: 'text-red-300 bg-red-500/10 border-red-500/25' }
        : { t: 'Working…', c: 'text-amber-200 bg-amber-500/10 border-amber-500/25' };
  return (
    <span className={`shrink-0 px-2 py-0.5 rounded-full border text-[10px] font-medium uppercase tracking-wide ${m.c}`}>
      {m.t}
    </span>
  );
}

// Renders a unified diff with simple +/- line coloring. Content is rendered as
// text (React escapes it), so untrusted repo/agent output cannot inject markup.
function DiffView({ diff }) {
  return (
    <pre className="px-3 py-2 text-[11px] leading-[1.5] font-mono overflow-auto max-h-80 whitespace-pre">
      {String(diff || '').split('\n').map((line, i) => {
        let cls = 'text-gray-400';
        if (line.startsWith('+') && !line.startsWith('+++')) cls = 'text-[#B0FC38]';
        else if (line.startsWith('-') && !line.startsWith('---')) cls = 'text-red-300';
        else if (line.startsWith('@@')) cls = 'text-[#B0FC38]';
        else if (line.startsWith('diff ') || line.startsWith('index ') || line.startsWith('+++') || line.startsWith('---')) cls = 'text-gray-600';
        return <div key={i} className={cls}>{line || ' '}</div>;
      })}
    </pre>
  );
}
